import { InputFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { isOtherMake, znackaRequiredTypes } from 'BrokerTsLib/src/validation'
import { useEffect, useMemo } from 'react'

import { FormRow } from '@/containers/forms/FormRow'
import { useGetMakers, useGetModels, useGetModelsBase } from '@/hooks/useVehicle'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'

export const VehicleModelSelection = ({
  isDisabled = false
}: {
  isDisabled?: boolean
}) => {
  const {
    control,
    setValue,
    watch,
    getValues
  } = useFormContext<InsuranceParamsDto>()

  const brokerMakeModelId = getValues('vehicle.brokerMakeModelId')
  const fuel = watch('vehicle.palivo')
  const category = watch('vehicle.druh')
  const make = watch('vehicle.znacka')
  const modelBase = watch('vehicle.zakladniModel')
  const model = watch('vehicle.model')

  const { data: makersData, isLoading: isLoadingMakers } = useGetMakers({ fuelId: fuel ?? 0, categoryId: category ?? 0, useBrokerMakeModel: !!brokerMakeModelId })

  const { data: modelBaseData, isLoading: usLoadingModelBases, isFetched } = useGetModelsBase({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: make ?? '', useBrokerMakeModel: !!brokerMakeModelId })

  const { data: modelsNotByBaseData } = useGetModels({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: make ?? '', useBrokerMakeModel: !!brokerMakeModelId })

  const { data: modelsData, isLoading: isLoadingModels } = useGetModels({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: make ?? '', modelBase, useBrokerMakeModel: !!brokerMakeModelId })

  // if model is not within modelsData, add it to options
  const modelOptions = useMemo(() => {
    const modelOptions = modelsData?.map((item) => ({ value: item, label: item }))
    if (model && modelsData && !modelsData.includes(model)) {
      modelOptions?.unshift({ value: model, label: model })
    }
    return modelOptions
  }, [modelsData, model])

  const tooMuchModels = (modelsNotByBaseData?.length ?? 0) > 600 && (modelBaseData?.length ?? 0) > 1

  const makeRequired = !!category && isIn(parseInt(category.toString()), ...znackaRequiredTypes)

  const showMakeText = (!makeRequired && !make) || isOtherMake(make)

  const onMakeChange = (value: string) => {
    setValue('vehicle.znackaText', '')
    setValue('vehicle.zakladniModel', '')
    setValue('vehicle.model', '')
  }

  const onModelBaseChange = (value: string) => {
    setValue('vehicle.model', '')
  }

  useEffect(() => {
    if (makersData?.length && !makersData?.some((item) => item.toUpperCase() === make?.toUpperCase()))
      setValue('vehicle.znacka', '')
  }, [makersData])

  useEffect(() => {
    if (make != 'OSTATNÍ')
      setValue('vehicle.znackaText', '')
  }, [make])

  useEffect(() => {
    if (modelBaseData?.length && !modelBaseData?.some((item) => item === modelBase))
      setValue('vehicle.zakladniModel', '')
  }, [modelBaseData])

  useEffect(() => {
    if (modelsData?.length === 1)
      setValue('vehicle.model', modelsData[0], { shouldValidate: true })
    else if (modelsData?.length && !modelsData?.some((item) => item === model))
      setValue('vehicle.model', '')
    else if (modelsData?.length == 0 && modelBase)
      setValue('vehicle.zakladniModel', '')
  }, [modelsData])

  return (
    <>
      <FormRow itemsCount={2}>
        <SelectFormControl name='vehicle.znacka' control={control}
          label='Značka' isRequired={makeRequired} isDisabled={isDisabled}
          onChange={(v) => onMakeChange(v as string)}
          valueTransform={(val) => val?.toString().toUpperCase()}
          isLoading={isLoadingMakers}
          unselectedText='Vyberte značku vozidla'
          options={makersData?.map((item) => ({ value: item.toUpperCase(), label: item.toUpperCase() }))}
          helperText={makeRequired && 'Zadejte tovární značku vozidla. V případě, že příslušná tovární značka není ve výběru, kontaktujte správce systému s žádostí o její přidání do číselníku.'}
        />

        {showMakeText &&
          <InputFormControl name='vehicle.znackaText' control={control}
            label='Tovární značka (text)'
            isDisabled={isDisabled}
            autoComplete='off'
            helperText="Zadejte tovární značku, pouze pokud nebyla vybrána ve výběru"
          />
        }
      </FormRow>

      <FormRow>
        <SelectFormControl name='vehicle.zakladniModel' control={control}
          label='Základní model'
          onChange={(v) => onModelBaseChange(v as string)}
          isDisabled={isDisabled || !make || !modelBaseData?.length}
          isLoading={usLoadingModelBases}
          isRequired={tooMuchModels}
          unselectedText={tooMuchModels ? 'Vyberte základní model' : ' '}
          options={modelBaseData?.map((item) => ({ value: item, label: item }))}
          helperText={tooMuchModels ? 'Pro vybranou značku je příliš mnoho možností k výběru. Prosím omezte počet výběrem základního modelu.' : 'Toto je pomocné pole pro výběr modelu vozidla a nemusí být vyplněno.'}
        />

        <SelectFormControl name='vehicle.model' control={control}
          label='Model'
          isDisabled={isDisabled || (tooMuchModels && !modelBase)}
          isLoading={isLoadingModels}
          isRequired
          unselectedText='Vyberte model'
          options={modelOptions}
          helperText="Zadejte model vozidla."
        />
      </FormRow>
    </>
  )
}
